import { EFinancingProgram } from '@if/core/types'
import { getApiClient } from '@src/services/api-client'
import { MutationFunction, useMutation, useQueryClient } from '@tanstack/react-query'
import { CreditApplication, FullCreditApplication } from '../../types'
import { fullCreditApplicationKeys } from '../full-credit-application/full-credit-application-api'
import { buildUrlWithIds, makeCreate, makeGetPdf } from '../make-api'
import { transformCreditApplicationFromApi } from '../credit-application/credit-transform'

const baseUrl = '/Applications/{financingProgramId}/{creditApplicationId}/Contract'

interface CreateCvtNumberDto {
  creditApplicationId: string
  financingProgramId: EFinancingProgram
}

// CREATE CVT NUMBER
async function createCvtNumberAsync(
  creditApplicationId: string,
  financingProgramId: EFinancingProgram,
): Promise<CreditApplication> {
  const apiClient = getApiClient()
  const url = buildUrlWithIds(baseUrl, { creditApplicationId, financingProgramId })
  const fn = makeCreate<CreateCvtNumberDto, CreditApplication>(url)
  return fn(apiClient, { creditApplicationId, financingProgramId })
}

export function useCreateCvtNumber(): [
  MutationFunction<CreditApplication, { creditApplicationId: string; financingProgramId: EFinancingProgram }>,
  boolean,
] {
  const queryClient = useQueryClient()

  const { mutateAsync, isPending } = useMutation<
    CreditApplication,
    Error,
    { creditApplicationId: string; financingProgramId: EFinancingProgram }
  >({
    mutationFn: ({ creditApplicationId, financingProgramId }) =>
      createCvtNumberAsync(creditApplicationId, financingProgramId),
    onSuccess: (updatedCredit, params) => {
      const queryKey = fullCreditApplicationKeys.detail(params.creditApplicationId, params.financingProgramId)
      const currentData = queryClient.getQueryData<FullCreditApplication>(queryKey)
      if (currentData) {
        queryClient.setQueryData<FullCreditApplication>(queryKey, {
          ...currentData,
          credit: transformCreditApplicationFromApi(updatedCredit),
        })
      }
    },
  })
  return [mutateAsync, isPending]
}

// DOWNLOAD CVT PDF
async function downloadCvtPdfAsync(creditApplicationId: string, financingProgramId: EFinancingProgram): Promise<void> {
  const apiClient = getApiClient()
  const url = buildUrlWithIds(baseUrl, { creditApplicationId, financingProgramId })
  const fn = makeGetPdf<CreateCvtNumberDto>(url)
  return fn(apiClient, { creditApplicationId, financingProgramId })
}

export function useDownloadCvtPdf(): [
  MutationFunction<void, { creditApplicationId: string; financingProgramId: EFinancingProgram }>,
  boolean,
] {
  const { mutateAsync, isPending } = useMutation({
    mutationFn: ({
      creditApplicationId,
      financingProgramId,
    }: {
      creditApplicationId: string
      financingProgramId: EFinancingProgram
    }) => downloadCvtPdfAsync(creditApplicationId, financingProgramId),
  })
  return [mutateAsync, isPending]
}
